import React from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, Zap } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import WhatsAppFloat from "../WhatsAppFloat";
import PageMeta from "../PageMeta";

export default function Pricing() {
  const { lang } = useParams();
  const { t } = useTranslation("pricing");

  const plans = t("plans", { returnObjects: true });

  return (
    <div className="min-h-screen bg-white">
      <PageMeta
        title={`${t("hero.title")} — TrackTeam`}
        description={t("hero.subtitle")}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 bg-gradient-to-br from-slate-50 via-blue-50/40 to-purple-50/30 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 rounded-full px-4 py-2 text-sm font-medium mb-6">
            <Zap className="w-4 h-4" />
            {t("hero.badge")}
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
            {t("hero.title")}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t("hero.subtitle")}
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {Array.isArray(plans) &&
              plans.map((plan, i) => (
                <div
                  key={i}
                  className={`rounded-2xl p-8 border-2 ${
                    plan.featured
                      ? "border-[#62329f] shadow-xl shadow-purple-100 relative"
                      : "border-gray-200"
                  }`}
                >
                  {plan.featured && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#62329f] text-white text-sm font-semibold px-4 py-1 rounded-full">
                      {t("popular")}
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-extrabold text-gray-900">{plan.price}</span>
                    {plan.period && <span className="text-gray-500 ml-1">{plan.period}</span>}
                  </div>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.washey.familytrack.trackteam&pcampaignid=web_share"
                    target="_blank"
                    rel="noreferrer"
                    className={`block text-center py-3 px-6 rounded-full font-semibold transition-all ${
                      plan.featured
                        ? "bg-[#62329f] text-white hover:bg-[#4f267f]"
                        : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                    }`}
                  >
                    {t("getStarted")}
                  </a>
                  <ul className="mt-6 space-y-3">
                    {Array.isArray(plan.features) &&
                      plan.features.map((feat, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600 text-sm">{feat}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t("faq.title")}</h2>
          {t("faq.items", { returnObjects: true }).map &&
            t("faq.items", { returnObjects: true }).map((item, i) => (
              <details key={i} className="mb-4 bg-white rounded-xl border border-gray-200 overflow-hidden">
                <summary className="px-6 py-4 cursor-pointer font-medium text-gray-900 hover:bg-gray-50">
                  {item.q}
                </summary>
                <div className="px-6 pb-4 text-gray-600">{item.a}</div>
              </details>
            ))}
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
